// src/components/Pages/ResellPipelinePageZustand.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Loader,
    Group,
    Alert,
    Center,
    Table,
    Select,
    Checkbox,
    ActionIcon,
    TextInput,
    Textarea,
    Modal,
    Button,
    Switch,
    Tooltip,
    Pagination,
    Radio,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
    IconReportAnalytics,
    IconAlertCircle,
    IconWorld,
    IconLanguage,
    IconRadar2,
    IconTrash,
    IconCheck,
    IconArchive,
    IconFileText
} from '@tabler/icons-react';
import { useShallow } from 'zustand/shallow';
import { notifications } from '@mantine/notifications';
import copy from 'copy-to-clipboard';
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import { useAuth } from '../../context/AuthContext'; // <-- ADDED useSearchParams
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useResellPipelineStore, { getFilteredAndSortedItems } from '../../stores/resellPipelineStore';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';

// ----------------- utils (hoisted and pure) -----------------
const CMS_EDIT_URL_BASE =
    'https://controlcentre.loveincorporated.com/msnglobal/Content/Gallery/Edit/';
//const CMS_RESELL_URL_BASE =
//    'https://controlcentre.loveincorporated.com/msnglobal/Content/Resell/Create/';
const CMS_RESELL_URL_PRELIVE_TEST =
    'https://controlcentre.loveincorporated.com/msnglobal/Content/Resell/Create/';

const normalizeDate = (date) => {
    if (!date) return null;
    if (typeof date?.toDate === 'function') return date.toDate();
    if (date instanceof Date) return date;
    if (typeof date === 'string') {
        const d = new Date(date);
        return isNaN(d) ? null : d;
    }
    return null;
};

const toISODateString = (date) => {
    const d = normalizeDate(date);
    return d ? d.toLocaleDateString('sv-SE') : null;
};

/**
 * Formats a date and time into the required UTC string for the CMS.
 * e.g., "2025-10-31T23:00:00"
 */
const formatDateForCMS = (date, timeStr) => {
    const d = normalizeDate(date);
    if (!d) return null; // No date, no URL

    const [hours = '00', minutes = '00'] = (timeStr || '00:00').split(':');

    // Set time on the date object
    d.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

    // Create a new Date object that represents the *local* YYYY-MM-DDTHH:mm:ss
    // without any timezone conversion, then slice it.
    // This avoids JS automatically converting it to UTC time, which might shift the day.

    // Get local date parts
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');

    // Manually construct the string
    return `${year}-${month}-${day}T${h}:${m}:00`;
};

/**
 * Sanitizes a string for TSV format by removing tabs and newlines.
 */
const sanitizeForTSV = (value) => {
    if (value === null || typeof value === 'undefined') {
        return '';
    }
    // Convert to string, replace tabs with space, and all newlines with space
    return String(value)
        .replace(/\t/g, ' ')
        .replace(/(\r\n|\n|\r)/g, ' ');
};

/**
 * Converts the array of pipeline items into a TSV string.
 */
const formatItemsForExcel = (items, usersMap) => {
    const headers = [
        'Date',
        'New Title',
        'Canonical ID',
        'Type',
        'Old Title',
        'Send Time',
        'New Image Slide',
        'Brand',
        'Last Entry',
        //'Variants',
        'Last Feed ID',
        //'Added By'
    ];

    // Create the header row, joined by tabs
    let tsv = headers.join('\t') + '\n';

    // Create a row for each item
    items.forEach(item => {
        const row = [
            item.pubDate ? toISODateString(item.pubDate) : '',
            item.newTitle,
            item.originalId,
            item.type,
            item.latestTitle,
            item.pubTime,
            item.newLeadImageSlideNo,
            item.Brand,
            item.lastEntryDate ? toISODateString(item.lastEntryDate) : '',
            //item.cdsEntries,
            item.lastFeedId,
            //usersMap[item.addedByUid] || 'Unknown'
        ];

        // Sanitize each value and join by tabs
        tsv += row.map(sanitizeForTSV).join('\t') + '\n';
    });

    return tsv;
};

const validateAndFormatTime = (timeStr) => {
    if (!timeStr) return { isValid: false, error: 'Time cannot be empty.' };
    const parts = timeStr.split(':');
    if (parts.length === 1 && /^\d{1,2}$/.test(parts[0])) {
        const h = +parts[0];
        if (h >= 0 && h <= 23) return { isValid: true, formattedTime: `${parts[0].padStart(2, '0')}:00` };
        return { isValid: false, error: 'Hour must be 0-23.' };
    }
    if (parts.length === 2) {
        const [hs, ms] = parts;
        if (!/^\d{1,2}$/.test(hs) || !/^\d{1,2}$/.test(ms)) {
            return { isValid: false, error: 'Use only numbers for HH and MM.' };
        }
        const h = +hs, m = +ms;
        if (h < 0 || h > 23) return { isValid: false, error: 'Hour must be 0-23.' };
        if (m < 0 || m > 59) return { isValid: false, error: 'Minute must be 0-59.' };
        return { isValid: true, formattedTime: `${hs.padStart(2, '0')}:${ms.padStart(2, '0')}` };
    }
    return { isValid: false, error: 'Invalid format. Use HH:MM.' };
};

const formatGroupHeaderDate = (date) => {
    const d = normalizeDate(date);
    if (!d) return 'Unscheduled';
    return d.toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const calculateDaysResting = (date) => {
    const lastDate = normalizeDate(date);
    if (!lastDate) return 'N/A';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfLastDate = new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate());
    const diff = startOfToday - startOfLastDate;
    return Math.floor(diff / (1000 * 3600 * 24));
};

// ----------------- Row -----------------
const PipelineRow = React.memo(function PipelineRow({
    item,
    isDark,
    theme,
    usersMap,
    feedsMap,
    selectedIds,
    completingId,
    handleRowSelect,
    handleFieldUpdate,
    handleTimeSegmentChange,
    setItemToComplete,
    setItemToDelete,
    setItemToArchive,
}) {
    const pubDateValue = normalizeDate(item.pubDate);
    const resellUrl = useMemo(() => {
        // 1. Get the base URL and article ID
        const baseUrl = `${CMS_RESELL_URL_PRELIVE_TEST}${item.lastFeedId}`;

        // 2. Create a URL parameter array
        const params = [];

        // 3. Add newTitle
        if (item.newTitle) {
            params.push(`newTitle=${encodeURIComponent(item.newTitle)}`);
        }

        // 4. Add selectedSlide (using the correct name)
        if (item.newLeadImageSlideNo) {
            // Use the parameter name from your CTO's email
            params.push(`selectedSlide=${item.newLeadImageSlideNo}`);
        }

        // 5. Add publishDate
        const cmsDate = formatDateForCMS(item.pubDate, item.pubTime);
        if (cmsDate) {
            params.push(`publishDate=${cmsDate}`);
        }

        // 6. Look up the feedId
        // We get region/language from the store's global state, not the item
        //const { region, language } = useResellPipelineStore.getState();
        const feedKey = `${item.Brand || ''}_${item.language || ''}_${item.region || ''}`;
        const feedId = feedsMap[feedKey];

        if (feedId) {
            params.push(`feedId=${feedId}`);
        } else {
            // Log if we couldn't find a feed.
            // Might want to disable the button, but for now, we'll
            // just let it link without the feedId.
            if (item.Brand) { // Only log if a brand was set
                console.warn(`Could not find feedId for key: ${feedKey}`);
            }
        }

        // 7. Add the new static parameter
        params.push('goliveimmediately=true');

        // 8. Join all parameters
        return `${baseUrl}?${params.join('&')}`;

    }, [
        item.lastFeedId,
        item.newTitle,
        item.newLeadImageSlideNo,
        item.pubDate,
        item.pubTime,
        item.Brand,
        feedsMap
        // region/language are read from getState(), so they don't need to be deps
    ]);
    const [hours, minutes] = (item.pubTime || ':').split(':');
    const isSelected = selectedIds.includes(item.id);
    const isSaving = false; // Placeholder, will be managed by local state if needed
    const handleSelect = useCallback(() => handleRowSelect(item.id), [item.id, handleRowSelect]);
    const handleComplete = useCallback(() => setItemToComplete(item), [item, setItemToComplete]);
    const handleDelete = useCallback(() => setItemToDelete(item.id), [item.id, setItemToDelete]);
    const handleArchive = useCallback(() => setItemToArchive(item), [item, setItemToArchive]);

    const handleNewTitleBlur = useCallback(
        (e) => handleFieldUpdate(item.id, 'newTitle', e.currentTarget.value),
        [item.id, handleFieldUpdate]
    );
    const handleTypeChange = useCallback(
        (v) => handleFieldUpdate(item.id, 'type', v),
        [item.id, handleFieldUpdate]
    );
    const handleDateChange = useCallback(
        (d) => handleFieldUpdate(item.id, 'pubDate', d),
        [item.id, handleFieldUpdate]
    );
    const handleHoursBlur = useCallback(
        (e) => handleTimeSegmentChange(item.id, 'hours', e.currentTarget.value),
        [item.id, handleTimeSegmentChange]
    );
    const handleMinutesBlur = useCallback(
        (e) => handleTimeSegmentChange(item.id, 'minutes', e.currentTarget.value),
        [item.id, handleTimeSegmentChange]
    );
    const handleSlideNoBlur = useCallback(
        (e) => {
            const rawValue = e.currentTarget.value;
            // Convert the input string to a number.
            const numValue = parseInt(rawValue, 10);

            if (!isNaN(numValue)) {
                // If it's a valid number, save it
                handleFieldUpdate(item.id, 'newLeadImageSlideNo', numValue);
            } else if (rawValue === '') {
                // If the user cleared the field, save null
                handleFieldUpdate(item.id, 'newLeadImageSlideNo', null);
            }
            // If they typed "abc", !isNaN is false and rawValue isn't '', so nothing happens.
        },
        [item.id, handleFieldUpdate]
    );

    return (
        <Table.Tr
            //verticalAlign="center"
            key={item.id}
            style={{
                verticalAlign: 'center',
                backgroundColor: isSelected ? (isDark ? theme.colors.dark[5] : theme.colors.gray[1]) : undefined
            }}
        >
            <Table.Td>
                <Checkbox checked={isSelected} onChange={handleSelect} />
            </Table.Td>
            <Table.Td>
                <Text size="xs">{item.latestTitle || 'Untitled'}</Text>
            </Table.Td>
            <Table.Td>
                <Textarea
                    placeholder="Enter new title..."
                    defaultValue={item.newTitle || ''}
                    onBlur={handleNewTitleBlur} disabled={isSaving}
                    size="xs"
                    autosize
                    minRows={1}
                    maxRows={4}
                />
            </Table.Td>
            <Table.Td>
                <Group justify="center">
                    <TextInput
                        placeholder="#"
                        defaultValue={item.newLeadImageSlideNo || ''}
                        onBlur={handleSlideNoBlur}
                        size="xs"
                        type="number" // Use HTML5 type="number" for better mobile/browser experience
                        styles={{ input: { width: 50, textAlign: 'center' } }} disabled={isSaving}
                    />
                </Group>
            </Table.Td>
            <Table.Td>{item.Brand || 'N/A'}</Table.Td>
            <Table.Td>
                <Select
                    data={['Resell', 'Major Update', 'Minor Update', 'New']}
                    value={item.type || 'Resell'}
                    onChange={handleTypeChange} disabled={isSaving}
                    size="xs"
                />
            </Table.Td>

            <Table.Td ta="center">{calculateDaysResting(item.lastEntryDate)}</Table.Td>
            <Table.Td ta="center" style={{ fontWeight: 500 }}>
                {item.refreshScore ? `${item.refreshScore.toFixed(1)}%` : 'N/A'}
            </Table.Td>

            <Table.Td style={{ textAlign: 'center' }}>
                {item.originalId}
            </Table.Td>
            <Table.Td ta="center">{item.cdsEntries ?? 'N/A'}</Table.Td>

            <Table.Td>
                <DatePickerInput
                    placeholder="Set date"
                    value={pubDateValue || null}
                    onChange={handleDateChange}
                    size="xs"
                    style={{ minWidth: 140 }} disabled={isSaving}
                />
            </Table.Td>
            <Table.Td ta="center">
                <Group justify="center" gap="xs" wrap="nowrap">
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }} tabIndex={-1}>
                        <TextInput
                            placeholder="HH"
                            defaultValue={hours}
                            onBlur={handleHoursBlur}
                            size="xs"
                            maxLength={2}
                            styles={{ input: { width: 45, textAlign: 'center' } }} disabled={isSaving}
                        />
                        <Text tabIndex={-1}>:</Text>
                        <TextInput
                            placeholder="MM"
                            defaultValue={minutes}
                            onBlur={handleMinutesBlur}
                            size="xs"
                            maxLength={2}
                            styles={{ input: { width: 45, textAlign: 'center' } }} disabled={isSaving}
                        />
                    </Box>
                </Group>
            </Table.Td>


            <Table.Td ta="center">
                {item.lastFeedId && (
                    <Group justify="center" gap="xs" wrap="nowrap">
                        <Tooltip label={`Edit Latest Variant: ${item.lastFeedId}`} withArrow>
                            <Button
                                component="a"
                                href={`${CMS_EDIT_URL_BASE}${item.lastFeedId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="compact-xs"
                                variant="outline"
                                style={{ minWidth: 70, justifyContent: 'center' }}
                            >
                                {item.lastFeedId}
                            </Button>
                        </Tooltip>
                        <Tooltip label={`Resell Latest Variant: ${item.lastFeedId}`} withArrow>
                            <Button
                                component="a"
                                //href={`${CMS_RESELL_URL_BASE}${item.lastFeedId}`}
                                href={resellUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="compact-xs"
                                variant="filled"
                                color="green"
                                style={{ minWidth: 70, justifyContent: 'center' }}
                            >
                                Resell
                            </Button>

                        </Tooltip>

                        <Tooltip label="Mark as done and remove from pipeline" withArrow>
                            <ActionIcon variant="light" color="green" onClick={handleComplete} loading={completingId === item.id}>
                                <IconCheck size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>

                )}

            </Table.Td>
            <Table.Td ta="center">
                {item.pageViews ? item.pageViews.toLocaleString() : 'N/A'}
            </Table.Td>

            <Table.Td>
                <Group justify="center" gap="xs" wrap="nowrap" style={{ align: 'center' }}>
                    <Tooltip label="Archive this item" withArrow>
                        <ActionIcon variant="light" color="blue" onClick={handleArchive}>
                            <IconArchive size={16} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Delete this item" withArrow>
                        <ActionIcon variant="subtle" color="red" tabIndex={-1} onClick={handleDelete}>
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Tooltip>
                    <Text size="xs">{usersMap[item.addedByUid] || '...'}</Text>
                </Group>
            </Table.Td>
           
        </Table.Tr>
    );
});

// ----------------- Page -----------------
function ResellPipelinePageZustand() {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentUser } = useAuth();
    const [isExcelCopied, setIsExcelCopied] = useState(false);

    // --- Zustand Store ---
    // Select state and actions. `useShallow` prevents re-renders if the selected object is shallowly equal.
    const {
        isLoading, error, usersMap,
        region, language, brand, showHistory, isGroupedView, showUnscheduledOnly,
        feedsMap,
        setRegion, setLanguage, setBrand, toggleShowHistory, toggleIsGroupedView, toggleShowUnscheduledOnly, initializeSubscriptions,
        cleanup, updateField, bulkUpdate, deleteItem, bulkDelete, archiveItem
    } = useResellPipelineStore(useShallow(state => ({
        isLoading: state.isLoading,
        error: state.error,
        //usersMap: state.usersMap,
        region: state.region,
        language: state.language,
        brand: state.brand,
        feedsMap: state.feedsMap,
        showHistory: state.showHistory,
        isGroupedView: state.isGroupedView,
        showUnscheduledOnly: state.showUnscheduledOnly,
        // Actions are functions and will not cause re-renders if selected this way with `useShallow`
        setRegion: state.setRegion,
        setLanguage: state.setLanguage,
        setBrand: state.setBrand,
        toggleShowHistory: state.toggleShowHistory,
        toggleIsGroupedView: state.toggleIsGroupedView,
        toggleShowUnscheduledOnly: state.toggleShowUnscheduledOnly,
        initializeSubscriptions: state.initializeSubscriptions,
        cleanup: state.cleanup,
        updateField: state.updateField,
        bulkUpdate: state.bulkUpdate,
        deleteItem: state.deleteItem,
        bulkDelete: state.bulkDelete, archiveItem: state.archiveItem,
    })));

    const filteredAndSortedItems = useResellPipelineStore(getFilteredAndSortedItems);
    // --- Local UI State (Modals, selections, etc.) ---
    const [selectedIds, setSelectedIds] = useState([]);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [bulkDate, setBulkDate] = useState(null);
    const [bulkTime, setBulkTime] = useState('');
    const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [bulkTimeError, setBulkTimeError] = useState(null);
    const [completingId, setCompletingId] = useState(null);
    const [itemToComplete, setItemToComplete] = useState(null);
    const [itemToArchive, setItemToArchive] = useState(null);
    const [bulkUpdateRevision, setBulkUpdateRevision] = useState(0);
    const [isGeneratingTitles, setIsGeneratingTitles] = useState(false);
    const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
    const [aiResults, setAiResults] = useState([]);
    const [selectedVariants, setSelectedVariants] = useState({});
    const { userProfile } = useAuth();

    const hasPrivilegedRole = useMemo(() =>
        userProfile && ['admin', 'management', 'editor'].includes(userProfile.role),
        [userProfile]
    );

    const [localUsersMap, setLocalUsersMap] = useState({});

    useEffect(() => {
        if (hasPrivilegedRole) {
            const db = getFirestore();
            const usersCollection = collection(db, 'users');
            const unsub = onSnapshot(usersCollection, (snapshot) => {

                const map = {};
                snapshot.forEach((d) => {
                    const u = d.data();
                    const firstName = u.firstName || '';
                    const lastName = u.lastName || '';
                    if (firstName && lastName) {
                        map[d.id] = `${firstName[0]}${lastName[0]}`; // e.g., "IM"
                    } else if (firstName) {
                        map[d.id] = `${firstName[0]}`; // e.g., "I"
                    } else {
                        map[d.id] = 'N/A';
                    }
                });
                setLocalUsersMap(map);
            });
            return () => unsub();
        }
    }, [hasPrivilegedRole]);

    // ---------- PAGINATION STATE ----------
    const [activePage, setPage] = useState(1);
    const ROWS_PER_PAGE = 50;

    // ---------- data transforms ----------
    const groupedItems = useMemo(() => {
        if (!isGroupedView) return null;
        return filteredAndSortedItems.reduce((acc, item) => {
            const key = formatGroupHeaderDate(item.pubDate);
            (acc[key] ||= []).push(item);
            return acc;
        }, {});
    }, [filteredAndSortedItems, isGroupedView]);



    // ---------- PAGINATION LOGIC ----------
    const totalPages = Math.ceil(filteredAndSortedItems.length / ROWS_PER_PAGE);
    const paginatedItems = useMemo(() => {
        const start = (activePage - 1) * ROWS_PER_PAGE;
        const end = start + ROWS_PER_PAGE;
        return filteredAndSortedItems.slice(start, end);
    }, [filteredAndSortedItems, activePage]);


    const itemIdsForSelection = useMemo(() => {
        const itemsForSelection = isGroupedView ? filteredAndSortedItems : paginatedItems;
        return itemsForSelection.map(i => i.id);
    }, [isGroupedView, filteredAndSortedItems, paginatedItems]);


    // Reset page to 1 when filters change
    useEffect(() => {
        setPage(1);
    }, [filteredAndSortedItems]);


    // ---------- handlers ----------
    const handleRowSelect = useCallback((id) => {
        setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
    }, []);

    const handleTimeSegmentChange = useCallback(
        (id, segment, value) => {
            const { items } = useResellPipelineStore.getState();
            const item = items.find(it => it.id === id);
            const curTime = item?.pubTime || ':';
            let [h, m] = curTime.split(':');
            if (segment === 'hours') h = String(value).padStart(2, '0');
            else m = String(value).padStart(2, '0');
            if (h && !m) m = '00';
            const newTime = `${h || ''}:${m || ''}`;
            updateField(id, 'pubTime', newTime);
        },
        [updateField]
    );

    // ---------- deletion / bulk ops ----------
    const handleConfirmRemove = () => {
        if (itemToArchive) {
            archiveItem(itemToArchive.id);
            setItemToArchive(null);
            return;
        }

        if (itemToDelete) {
            deleteItem(itemToDelete);
            setItemToDelete(null);
        }
    };

    const handleConfirmBulkUpdate = (field, value) => {
        if (selectedIds.length > 0 && value !== null && value !== '') {
            bulkUpdate(selectedIds, field, value);
            setSelectedIds([]);
            setBulkDate(null);
            setBulkTime('');
            if (field === 'pubDate') setBulkDate(null);
            if (field === 'pubTime') setBulkTime('');
            setBulkUpdateRevision(rev => rev + 1);
        }
    };

    const handleConfirmBulkRemove = () => {
        if (selectedIds.length > 0) {
            bulkDelete(selectedIds);
            setSelectedIds([]);
            setBulkDeleteModalOpen(false);
        }
    };

    // TODO: This logic needs to be moved into the store as an action
    const handleMarkAsComplete = async () => {
        if (!itemToComplete) return;
        setCompletingId(itemToComplete.id);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(
                'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/markItemAsComplete',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ docId: itemToComplete.id }),
                }
            );
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || 'Failed to mark item as complete.');
            }
            notifications.show({
                title: 'Item Completed',
                message: 'The article has been moved to your completed list.',
                color: 'green',
            });
        } catch (e) {
            notifications.show({ title: 'Error', message: e.message, color: 'red' });
        } finally {
            setCompletingId(null);
            setItemToComplete(null);
        }
    };

    const handleGenerateTitles = async () => {
        if (selectedIds.length === 0 || !currentUser) return;

        setIsGeneratingTitles(true);
        setAiResults([]); // Clear previous results
        setSelectedVariants({}); // Clear previous selections

        try {
            const { region, language, items } = useResellPipelineStore.getState();
            const selectedItems = items.filter(item => selectedIds.includes(item.id));
            const payload = {
                region: region,
                language: language,
                titles: selectedItems.map(item => ({
                    id: item.id,
                    original_title: item.latestTitle,
                    brand: item.Brand
                }))
            };

            const token = await currentUser.getIdToken();
            const res = await fetch(
                'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/generateSimpleTitles',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify(payload),
                }
            );

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || 'Failed to generate titles.');
            }

            if (data.results && data.results.length > 0) {
                // Save results to state and open the modal
                setAiResults(data.results);
                setIsTitleModalOpen(true);
            } else {
                throw new Error("The AI returned no results.");
            }

        } catch (err) {
            console.error('Error generating titles:', err);
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
        } finally {
            setIsGeneratingTitles(false);
        }
    };

    const handleApplySelections = () => {
        const { updateField } = useResellPipelineStore.getState();
        const choices = Object.entries(selectedVariants);

        if (choices.length > 0) {
            choices.forEach(([id, newTitle]) => {
                updateField(id, 'newTitle', newTitle);
            });

            notifications.show({
                title: 'Titles Updated',
                message: `Successfully set ${choices.length} new title(s).`,
                color: 'green',
            });
        }

        // Clean up and close modal
        setIsTitleModalOpen(false);
        setAiResults([]);
        // Force a re-render of the table body to show the new titles
        setBulkUpdateRevision(rev => rev + 1);
        setSelectedVariants({});
        setSelectedIds([]);
    };

    const handleCopyToExcel = () => {
        // This is the key: we get 'items' directly from the store's state.
        // This gives us the full list based on the current filters (region, lang, brand)
        // *before* the UI toggles (like "Show Past") are applied.
        const { items } = useResellPipelineStore.getState();

        // We use the localUsersMap we already have
        const tsvString = formatItemsForExcel(items, localUsersMap);

        try {
            copy(tsvString, { format: 'text/plain' }); // <-- ADD THIS OPTION
            setIsExcelCopied(true);
            setTimeout(() => setIsExcelCopied(false), 2000); // Reset after 2s

            notifications.show({
                title: 'Copied to Clipboard',
                message: `${items.length} rows copied. You can now paste into Excel.`,
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: 'Copy Failed',
                message: 'Could not copy data to clipboard. See console for error.',
                color: 'red',
            });
            console.error("Failed to copy for Excel:", err);
        }
    };

    // ---------- subscriptions ----------
    // Initialize subscriptions on mount and clean up on unmount.
    // Filter changes are now handled directly within the store's actions.
    useEffect(() => {
        initializeSubscriptions();
        return () => cleanup();
    }, []); // <-- CRITICAL: Run only on mount and unmount
    // Handle incoming query parameters to set initial filter state
    useEffect(() => {
        const regionParam = searchParams.get('region');
        const languageParam = searchParams.get('language');
        const brandParam = searchParams.get('brand');

        if (regionParam) {
            setRegion(regionParam);
        }
        if (languageParam) {
            setLanguage(languageParam);
        }
        if (brandParam) {
            setBrand(brandParam);
        }

        // Clean up URL params after applying them so they don't persist on refresh
        if (regionParam || languageParam || brandParam) {
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams, setRegion, setLanguage, setBrand]);

    
    // Clear selection whenever filters change to prevent cross-filter accidental actions
    useEffect(() => {
        setSelectedIds([]);
    }, [region, language, brand]);
    
    
    // ---------- memo for stable props ----------
    const commonRowProps = useMemo(
        () => ({
            isDark,
            theme,
            usersMap: localUsersMap, // Was2: usersMap: usersMap, // Was1: usersMap: hasPrivilegedRole ? localUsersMap : usersMap,
            feedsMap,
            selectedIds,
            handleFieldUpdate: updateField,
            completingId,
            handleRowSelect,
            handleTimeSegmentChange,
            setItemToComplete,
            setItemToDelete,
            setItemToArchive,
        }),
        [
            isDark,
            theme,
            localUsersMap, // Was2: usersMap, // Was1: hasPrivilegedRole ? localUsersMap : usersMap,
            feedsMap,
            selectedIds,
            updateField, // Ensures re-memoization if the function reference changes
            completingId,
            handleRowSelect,
            handleTimeSegmentChange,
            bulkUpdateRevision
        ]
    );

    // ---------- content ----------
    const renderContent = () => {
        if (isLoading) {
            return (
                <Center mt="xl" p="xl">
                    <Loader />
                    <Text ml="md">Loading Pipeline...</Text>
                </Center>
            );
        }
        if (error) {
            return (
                <Alert icon={<IconAlertCircle size="1rem" />} title="Error!" color="red" mt="xl">
                    {error}
                </Alert>
            );
        }
        if (paginatedItems.length === 0) { // Check paginated items
            return (
                <Center mt="xl" p="xl">
                    <Text c="dimmed">No items found for the selected filters.</Text>
                </Center>
            );
        }

        // const itemsForSelection = isGroupedView ? filteredAndSortedItems : paginatedItems;
        // const itemIdsForSelection = useMemo(() => itemsForSelection.map(i => i.id), [itemsForSelection]);

        // 2. Update checkbox state to use the correct list
        const allOnPageSelected = itemIdsForSelection.length > 0 && itemIdsForSelection.every(id => selectedIds.includes(id));
        const someOnPageSelected = itemIdsForSelection.some(id => selectedIds.includes(id)) && !allOnPageSelected;

        const handleSelectAll = () => {
            const pageIds = itemIdsForSelection; // Use the memoized list of IDs
            if (allOnPageSelected) {
                setSelectedIds(current => current.filter(id => !pageIds.includes(id)));
            } else {
                setSelectedIds(current => [...new Set([...current, ...pageIds])]);
            }
        };

        //  const allOnPageSelected = paginatedItems.length > 0 && paginatedItems.every(i => selectedIds.includes(i.id));
        //  const someOnPageSelected = paginatedItems.some(i => selectedIds.includes(i.id)) && !allOnPageSelected;

        //  const handleSelectAll = () => {
        //  const pageIds = paginatedItems.map(i => i.id);
        //  if (allOnPageSelected) {
        //  setSelectedIds(current => current.filter(id => !pageIds.includes(id)));
        //  } else {
        //  setSelectedIds(current => [...new Set([...current, ...pageIds])]);
        //  }
        //  };

        // Determine which items to render based on view type
        const itemsToRender = isGroupedView ? groupedItems : paginatedItems;

        return (
            <>
                <Table.ScrollContainer minWidth={1800}>
                    <Table
                        striped
                        stickyHeader
                        highlightOnHover
                        verticalSpacing="sm"
                        layout="fixed"
                    >
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{ width: 40, position: 'sticky', top: 0, zIndex: 1, backgroundColor: isDark ? theme.colors.dark[7] : theme.white }}>
                                    <Checkbox
                                        checked={allOnPageSelected}
                                        indeterminate={someOnPageSelected}
                                        onChange={handleSelectAll}
                                    />
                                </Table.Th>
                                <Table.Th style={{ width: 200, position: 'sticky', top: 0, zIndex: 1, backgroundColor: isDark ? theme.colors.dark[7] : theme.white }}>Article Title (Last Used)</Table.Th>
                                <Table.Th style={{ width: 220, position: 'sticky', top: 0, zIndex: 1, backgroundColor: isDark ? theme.colors.dark[7] : theme.white }}>New Title</Table.Th>
                                <Tooltip label="Enter the slide number that contains the new lead image." withArrow><Table.Th style={{ width: 90, textAlign: 'center' }}>

                                    New
                                    <br />
                                    Image

                                </Table.Th></Tooltip>
                                <Table.Th style={{ width: 130 }}>Brand</Table.Th>
                                <Table.Th style={{ width: 150 }}>Type</Table.Th>

                                <Table.Th style={{ width: 75, textAlign: 'center' }}>Days Resting</Table.Th>
                                <Table.Th style={{ width: 75, textAlign: 'center' }}>Score</Table.Th>
                                <Table.Th style={{ width: 70, textAlign: 'center' }}>Original ID</Table.Th>
                                <Table.Th style={{ width: 75, textAlign: 'center' }}>Variants</Table.Th>

                                <Table.Th style={{ width: 150, textAlign: 'center' }}>Send Date</Table.Th>
                                <Table.Th style={{ width: 130, textAlign: 'center' }}>Send Time</Table.Th>

                                <Table.Th style={{ width: 190, textAlign: 'center' }}>
                                    Last Variant
                                    <br />
                                    (Clear if primed in CMS)
                                </Table.Th>
                                <Table.Th style={{ width: 90, textAlign: 'center' }}>Page Views</Table.Th>
                                <Table.Th style={{ width: 100, textAlign: 'center' }}>Actions</Table.Th>
                                
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody key={bulkUpdateRevision}>
                            {isGroupedView
                                ? Object.keys(itemsToRender).map((dateKey) => (
                                    <React.Fragment key={dateKey}>
                                        <Table.Tr
                                            style={{
                                                backgroundColor: isDark ? theme.colors.blue[9] : theme.colors.blue[2],
                                                color: isDark ? theme.colors.blue[1] : theme.colors.blue[8],
                                                position: 'sticky', top: 38, zIndex: 1 // Sticky header height
                                            }}
                                        >
                                            <Table.Td colSpan={15} fw={700}>
                                                {dateKey}
                                            </Table.Td>
                                        </Table.Tr>
                                        {itemsToRender[dateKey].map((item) => (
                                            <PipelineRow
                                                key={item.id}
                                                item={item}
                                                isSelected={selectedIds.includes(item.id)}
                                                {...commonRowProps}
                                            />
                                        ))}
                                    </React.Fragment>
                                ))
                                : itemsToRender.map((item) => (
                                    <PipelineRow
                                        key={item.id} item={item}
                                        isSelected={selectedIds.includes(item.id)}
                                        {...commonRowProps} />
                                ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>

                {totalPages > 1 && !isGroupedView && (
                    <Group justify="center" mt="xl">
                        <Pagination total={totalPages} value={activePage} onChange={setPage} />
                    </Group>
                )}
            </>
        );
    };

    // ---------- layout ----------
    return (
        <Box
            style={{
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                // KEY STABILITY FIX: Use a non-fixed background to avoid Chrome resize jank
                backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
                backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'scroll', // Use 'scroll' instead of 'fixed'
            }}
        >
            <Container size="xl" py="xl">
                <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                    <IconReportAnalytics
                        color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]}
                        style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }}
                    />
                    Resell Pipeline
                </Title>
                <Text size="xl" ta="center" mb="xl" style={{ color: headerTextColor }}>
                    Manage articles selected for republication.
                </Text>

                <Paper withBorder shadow="sm" p="md" radius="md">
                    <Group grow>
                        <Select
                            label="Region"
                            leftSection={<IconWorld size={16} />}
                            data={[
                                'UK', 'US', '--', 'AE', 'AR', 'AT', 'AU', 'BE', 'CA', 'CH', 'CL', 'CO', 'DE',
                                'ES', 'FR', 'IE', 'IN', 'MX', 'MY', 'NZ', 'PE', 'PH', 'SG', 'XL', 'ZA',
                            ]}
                            value={region}
                            onChange={(v) => setRegion(v || 'US')}
                        />
                        <Select
                            label="Language"
                            leftSection={<IconLanguage size={16} />}
                            data={['EN', 'DE', 'ES', 'FR']}
                            value={language}
                            onChange={(v) => setLanguage(v || 'EN')}
                        />
                        <Select
                            label="Brand"
                            leftSection={<IconRadar2 size={16} />}
                            data={['All', 'loveMONEY', 'loveFOOD', 'loveEXPLORING', 'lovePROPERTY']}
                            value={brand}
                            onChange={(v) => setBrand(v || 'All')}
                        />
                    </Group>
                    <Group justify="space-between" mt="md" align="flex-end">
                        <Group>
                            <Switch
                                label="Show Past Items"
                                checked={showHistory}
                                onChange={(e) => toggleShowHistory(e.currentTarget.checked)}
                                disabled={showUnscheduledOnly}
                            />
                            <Switch
                                label="Group by Date"
                                checked={isGroupedView}
                                onChange={(e) => toggleIsGroupedView(e.currentTarget.checked)}
                            />
                            <Switch
                                label="Unscheduled Only"
                                checked={showUnscheduledOnly}
                                onChange={(e) => toggleShowUnscheduledOnly(e.currentTarget.checked)}
                            />
                        </Group>
                        <Button
                            variant="outline"
                            color={isExcelCopied ? 'teal' : 'green'}
                            onClick={handleCopyToExcel}
                            leftSection={isExcelCopied ? <IconCheck size={16} /> : <IconFileText size={16} />}
                        >
                            {isExcelCopied ? 'Copied!' : 'Copy All for Excel'}
                        </Button>
                        <Button variant="outline" onClick={() => navigate(`/resell-radar?region=${region}&language=${language}&brand=${brand}`)}>
                            Go to Resell Radar
                        </Button>
                    </Group>
                </Paper>
            </Container>

            {selectedIds.length > 0 && (
                <Container size="xl" pb="md">
                    <Paper withBorder shadow="sm" p="md" radius="md">
                        <Group align="flex-end" justify="space-between">
                            <Text fw={500}>{selectedIds.length} item(s) selected</Text>
                            <Group>
                                <DatePickerInput
                                    placeholder="Set date for selected"
                                    value={bulkDate}
                                    onChange={setBulkDate}
                                    size="xs"
                                    clearable
                                />
                                <Button
                                    size="xs"
                                    onClick={() => handleConfirmBulkUpdate('pubDate', bulkDate)}
                                    disabled={!bulkDate}
                                >
                                    Set Date
                                </Button>
                                <TextInput
                                    placeholder="HH:MM"
                                    value={bulkTime}
                                    onChange={(e) => {
                                        const t = e.currentTarget.value;
                                        setBulkTime(t);
                                        const v = validateAndFormatTime(t);
                                        setBulkTimeError(v.isValid ? null : v.error);
                                    }}
                                    size="xs"
                                    error={bulkTimeError}
                                    styles={{ input: { width: 80, textAlign: 'center' } }}
                                />
                                <Button
                                    size="xs"
                                    onClick={() => {
                                        const v = validateAndFormatTime(bulkTime);
                                        if (v.isValid) handleConfirmBulkUpdate('pubTime', v.formattedTime);
                                        else setBulkTimeError(v.error);
                                    }}
                                    disabled={!bulkTime || !!bulkTimeError}
                                >
                                    Set Time
                                </Button>

                                <Button
                                    size="xs"
                                    color="green"
                                    variant="filled"
                                    onClick={handleGenerateTitles}
                                    loading={isGeneratingTitles}
                                    disabled={isGeneratingTitles || selectedIds.length === 0}
                                >
                                    Generate AI Titles
                                </Button>
                                <Button
                                    size="xs"
                                    color="red"
                                    variant="outline"
                                    onClick={() => setBulkDeleteModalOpen(true)}
                                >
                                    Remove Selected
                                </Button>
                            </Group>
                        </Group>
                    </Paper>
                </Container>
            )}

            <Container fluid px="xl" pb="xl">
                <Paper withBorder shadow="sm" radius="md" p="md">
                    {/* Note: Switched to p="md" for padding */}
                    {renderContent()}
                </Paper>
            </Container>

            {/* Modals */}
            <Modal opened={!!itemToDelete} onClose={() => setItemToDelete(null)} title="Confirm Removal" centered size="sm">
                <Text size="sm">Are you sure you want to remove this article from the pipeline?</Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setItemToDelete(null)}>
                        Cancel
                    </Button>
                    <Button color="red" onClick={handleConfirmRemove}>
                        Remove Item
                    </Button>
                </Group>
            </Modal>

            <Modal opened={!!itemToArchive} onClose={() => setItemToArchive(null)} title="Confirm Archive" centered size="sm">
                <Text size="sm">Are you sure you want to archive this article?</Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setItemToArchive(null)}>
                        Cancel
                    </Button>
                    <Button color="blue" onClick={handleConfirmRemove}>
                        Archive Item
                    </Button>
                </Group>
            </Modal>

            <Modal
                opened={isBulkDeleteModalOpen}
                onClose={() => setBulkDeleteModalOpen(false)}
                title="Confirm Bulk Removal"
                centered
                size="sm"
            >
                <Text size="sm">
                    Are you sure you want to remove the {selectedIds.length} selected articles from the pipeline? This action
                    cannot be undone.
                </Text>
                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={() => setBulkDeleteModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="red" onClick={handleConfirmBulkRemove}>
                        Remove Items
                    </Button>
                </Group>
            </Modal>

            <Modal opened={!!itemToComplete} onClose={() => setItemToComplete(null)} title="Confirm Completion" centered size="md">
                <Text fw={700} my="md" style={{ fontStyle: 'italic' }}>
                    {itemToComplete?.latestTitle}
                </Text>
                <Text size="md">
                    Please confirm that you have scheduled the next incarnation of this in the CMS before removing it from the
                    pipeline...
                </Text>
                <Group justify="flex-end" mt="xl">
                    <Button variant="default" onClick={() => setItemToComplete(null)} disabled={!!completingId}>
                        Cancel
                    </Button>
                    <Button color="green" onClick={handleMarkAsComplete} loading={!!completingId}>
                        Complete
                    </Button>
                </Group>
            </Modal>

            <Modal
                opened={isTitleModalOpen}
                onClose={() => setIsTitleModalOpen(false)}
                title="Select New Titles"
                centered
                size="xl"
                scrollAreaComponent={Paper}
            >
                <Box style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '1rem' }}>
                    {aiResults.map((result) => (
                        <Paper withBorder p="md" mb="md" key={result.id}>
                            <Text size="sm" c="dimmed">Original Title</Text>
                            <Text fw={700} mb="sm" style={{ fontStyle: 'italic' }}>
                                {result.original_title}
                            </Text>
                            <Radio.Group
                                label="Choose one variant:"
                                value={selectedVariants[result.id] || null}
                                onChange={(value) =>
                                    setSelectedVariants(current => ({ ...current, [result.id]: value }))
                                }
                            >
                                <Group mt="xs">
                                    {result.new_variants.map((variant, index) => (
                                        <Radio key={index} value={variant} label={variant} />
                                    ))}
                                </Group>
                                
                                {/* --- START MODIFICATION --- */}
                                <Radio
                                    key="manual-review"
                                    value="*Manual Review Required*"
                                    // Use the label prop to pass a styled Text component
                                    label={
                                        <Text c="orange" fw={500} size="sm">
                                            *Manual Review Required*
                                        </Text>
                                    }
                                    mt="sm" // Adds margin-top for spacing
                                />
                                {/* --- END MODIFICATION --- */}
                                
                            </Radio.Group>
                        </Paper>
                    ))}
                </Box>
                <Group justify="flex-end" mt="xl">
                    <Button variant="default" onClick={() => setIsTitleModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="green" onClick={handleApplySelections}>
                        Set Titles
                    </Button>
                </Group>
            </Modal>
        </Box>
    );
}

export default ResellPipelinePageZustand;
